import React from 'react';
import Select, { StylesConfig } from 'react-select';
import { readableColor } from 'polished';

const dot = (color = 'transparent') => ({
  alignItems: 'center',
  display: 'flex',
  ':before': {
    backgroundColor: color,
    borderRadius: 2,
    content: '" "',
    display: 'block',
    marginRight: 8,
    height: 10,
    width: 10,
    border: '1px solid #ddd'
  }
});

const isIOS = typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

function toggleBodyScroll(lock) {
  if (!isIOS) return;
  if (lock) {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
  } else {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
  }
}

const colorStyles = {
  container: (styles) => ({ ...styles }),
  control: (styles) => ({
    ...styles,
    borderColor: '#ddd',
    fontSize: isIOS ? '16px' : styles.fontSize
  }),
  input: (styles) => ({
    ...styles,
    fontSize: isIOS ? '16px' : styles.fontSize
  }),
  option: (styles, { data }) => ({
    ...styles,
    borderColor: '#ddd',
    color: '#000',
    fontSize: isIOS ? '16px' : styles.fontSize,
    ...dot(data.color)
  }),
  singleValue: (styles, { data }) => ({
    ...styles,
    color: '#000',
    ...dot(data.color),
    fontSize: isIOS ? '16px !important' : styles.fontSize
  }),
  multiValue: (styles, { data }) => ({
    ...styles,
    backgroundColor: data.color || '#fff',
    color: readableColor(data.color || '#fff'),
    borderRadius: 4,
    fontSize: isIOS ? '16px !important' : styles.fontSize
  }),
  multiValueLabel: (styles, { data }) => ({
    ...styles,
    color: readableColor(data.color || '#fff'),
    fontSize: isIOS ? '16px !important' : styles.fontSize
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    ':hover': {
      backgroundColor: 'rgba(0,0,0,.2)'
    }
  }),
  menu: (styles) => ({
    ...styles,
    zIndex: 9999,
    position: isIOS ? 'fixed' : styles.position,
    width: isIOS ? '90vw' : styles.width,
    left: isIOS ? '5vw' : styles.left,
    right: isIOS ? '5vw' : styles.right,
    top: isIOS ? undefined : styles.top,
    maxHeight: isIOS ? '300px' : styles.maxHeight,
    background: '#fff',
    borderRadius: 8,
    boxShadow: styles.boxShadow
  }),
  menuPortal: (styles) => ({
    ...styles,
    zIndex: 9999,
    ...(isIOS && {
      position: 'fixed',
      left: '5vw',
      width: '90vw',
      background: '#fff',
      borderRadius: 8
    })
  }),
  menuList: (styles) => ({
    ...styles,
    maxHeight: isIOS ? '300px' : styles.maxHeight,
    overflowY: 'auto'
  })
};

export default ({ name, value, field, onChange, ...props }) => {
  const options = (field.data || []).map(({ label, color = '#fff' }) => ({
    label,
    value: label,
    color
  }));

  const isMulti = field.type === 'multiselect' ? true : false;
  const selections = [].concat(value || '');
  const selected = options.filter((o) => selections.includes(o.value));

  const [menuIsOpen, setMenuIsOpen] = React.useState(false);

  React.useEffect(() => {
    if (isIOS) toggleBodyScroll(menuIsOpen);
    return () => {
      if (isIOS) toggleBodyScroll(false);
    };
  }, [menuIsOpen]);

  return (
    <Select
      value={isMulti ? selected : selected[0]}
      options={options}
      styles={colorStyles}
      onChange={(selections) => {
        const value = isMulti ? selections.map((s) => s.value) : selections.value;
        onChange && onChange({ target: { value, name } });
      }}
      isMulti={isMulti}
      controlShouldRenderValue
      hideSelectedOptions
      captureMenuScroll={false}
      closeMenuOnSelect={!isMulti}
      maxMenuHeight={300}
      menuPlacement="auto"
      closeMenuOnScroll={isIOS ? false : true}
      menuPosition={isIOS ? 'fixed' : 'absolute'}
      menuPortalTarget={isIOS ? document.body : undefined}
      menuShouldScrollIntoView
      menuIsOpen={menuIsOpen}
      onMenuOpen={() => setMenuIsOpen(true)}
      onMenuClose={() => setMenuIsOpen(false)}
      {...props}
    />
  );
};
